<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { X } from '@lucide/vue'
import { basicSetup, EditorView } from 'codemirror'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { lintGutter, linter } from '@codemirror/lint'

const props = withDefaults(defineProps<{
  open: boolean
  /** 当前草稿的格式化 JSON 文本，对话框打开时重置进编辑器 */
  schema: string
  /** 后端 DRAFT 校验失败时由父组件传入的错误消息 */
  error?: string
  /** 父组件正在执行校验/回写时禁用操作按钮 */
  applying?: boolean
}>(), {
  error: '',
  applying: false
})

const emit = defineEmits<{
  close: []
  apply: [schemaText: string]
}>()

const dialogElement = ref<HTMLElement | null>(null)
const closeButtonElement = ref<HTMLButtonElement | null>(null)
const editorHost = ref<HTMLDivElement | null>(null)
const parseError = ref('')
let view: EditorView | null = null
let returnFocus: HTMLElement | null = null

watch(() => props.open, (open) => {
  if (open) {
    returnFocus = activeHtmlElement()
    parseError.value = ''
    void mountEditorAfterRender()
    return
  }
  destroyEditor()
  restoreDialogFocus(returnFocus)
  returnFocus = null
})

onBeforeUnmount(() => {
  destroyEditor()
})

async function mountEditorAfterRender() {
  await nextTick()
  const host = editorHost.value
  if (!host || !props.open) return
  destroyEditor()
  view = new EditorView({
    doc: props.schema ?? '',
    extensions: [
      basicSetup,
      json(),
      lintGutter(),
      linter(jsonParseLinter()),
      EditorView.lineWrapping,
      EditorView.theme({
        '&': { minHeight: '320px', maxHeight: '52vh', height: '100%' },
        '.cm-scroller': {
          overflow: 'auto',
          fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
          fontSize: '13px',
          lineHeight: '1.5'
        }
      })
    ],
    parent: host
  })
  const dialog = dialogElement.value
  const target = closeButtonElement.value ?? dialog
  target?.focus()
}

function destroyEditor() {
  view?.destroy()
  view = null
}

function editorText() {
  return view ? view.state.doc.toString() : ''
}

function replaceEditorText(text: string) {
  if (!view) return
  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: text }
  })
}

function formatSource() {
  const text = editorText()
  try {
    replaceEditorText(JSON.stringify(JSON.parse(text), null, 2))
    parseError.value = ''
  } catch (err) {
    parseError.value = `JSON 格式错误：${err instanceof Error ? err.message : '无法解析当前内容'}`
  }
}

const copied = ref(false)
async function copySource() {
  const text = editorText()
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1600)
  } catch {
    parseError.value = '复制失败，请手动全选复制。'
  }
}

function applySource() {
  if (props.applying) return
  const text = editorText()
  try {
    JSON.parse(text)
  } catch (err) {
    parseError.value = `JSON 格式错误：${err instanceof Error ? err.message : '无法解析当前内容'}`
    return
  }
  parseError.value = ''
  emit('apply', text)
}

function closeDialog() {
  if (props.applying) return
  emit('close')
}

function handleDialogKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented) return
  const dialog = dialogElement.value
  if (!dialog) return
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    closeDialog()
    return
  }
  if (event.key !== 'Tab') return
  const focusable = dialogFocusableElements(dialog)
  if (focusable.length === 0) {
    event.preventDefault()
    dialog.focus()
    return
  }
  const active = activeHtmlElement()
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && (active === first || !active || !dialog.contains(active))) {
    event.preventDefault()
    last?.focus()
  } else if (!event.shiftKey && (active === last || !active || !dialog.contains(active))) {
    event.preventDefault()
    first?.focus()
  }
}

function dialogFocusableElements(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>([
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '.cm-content'
  ].join(','))).filter((element) => element.tabIndex >= 0 && !element.hidden)
}

function activeHtmlElement() {
  return typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null
}

function restoreDialogFocus(target: HTMLElement | null) {
  if (!target) return
  void nextTick(() => {
    if (target.isConnected) target.focus()
  })
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.open"
      class="decoration-source-dialog__mask"
      @click.self="closeDialog"
      @keydown="handleDialogKeydown"
    >
      <section
        ref="dialogElement"
        class="decoration-source-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="页面源码"
        tabindex="-1"
      >
        <header>
          <div>
            <h3>页面源码</h3>
            <span>查看或编辑当前草稿的 schema JSON，应用前会先通过 DRAFT 模式校验。</span>
          </div>
          <button
            ref="closeButtonElement"
            type="button"
            title="关闭"
            aria-label="关闭"
            :disabled="props.applying"
            @click="closeDialog"
          >
            <X :size="20" />
          </button>
        </header>

        <div ref="editorHost" class="decoration-source-dialog__editor"></div>

        <p v-if="parseError" class="decoration-source-dialog__error" role="alert">{{ parseError }}</p>
        <p v-else-if="props.error" class="decoration-source-dialog__error" role="alert">{{ props.error }}</p>

        <footer>
          <button type="button" :disabled="props.applying" @click="formatSource">格式化</button>
          <button type="button" :disabled="props.applying" @click="copySource">{{ copied ? '已复制' : '复制' }}</button>
          <button type="button" :disabled="props.applying" @click="closeDialog">取消</button>
          <button class="is-primary" type="button" :disabled="props.applying" @click="applySource">
            {{ props.applying ? '校验中...' : '应用' }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.decoration-source-dialog__mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.48);
}

.decoration-source-dialog {
  display: grid;
  gap: 14px;
  width: min(880px, 100%);
  max-height: calc(100vh - 48px);
  padding: 20px;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
}

.decoration-source-dialog:focus {
  outline: none;
}

.decoration-source-dialog header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.decoration-source-dialog header h3 {
  margin: 0;
  font-size: 17px;
}

.decoration-source-dialog header span {
  display: block;
  margin-top: 4px;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.decoration-source-dialog header button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  color: var(--rr-color-text);
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.decoration-source-dialog header button:hover {
  background: var(--rr-color-surface-subtle);
}

.decoration-source-dialog__editor {
  border: 1px solid var(--rr-color-border);
  border-radius: 10px;
  overflow: hidden;
}

.decoration-source-dialog__editor :deep(.cm-editor.cm-focused) {
  outline: none;
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 3px var(--rr-color-primary-soft);
}

.decoration-source-dialog__error {
  margin: 0;
  padding: 10px 12px;
  color: var(--rr-color-danger);
  font-size: 13px;
  background: var(--rr-color-danger-soft);
  border: 1px solid var(--rr-color-danger-border);
  border-radius: 8px;
  white-space: pre-line;
  white-space: pre-wrap;
  word-break: break-all;
}

.decoration-source-dialog footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.decoration-source-dialog footer button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  color: var(--rr-color-text);
  background: #fff;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 8px;
  cursor: pointer;
}

.decoration-source-dialog footer button:hover:not(:disabled) {
  background: var(--rr-color-surface-subtle);
}

.decoration-source-dialog footer button.is-primary {
  color: #fff;
  background: var(--rr-color-primary);
  border-color: var(--rr-color-primary);
}

.decoration-source-dialog footer button.is-primary:hover:not(:disabled) {
  background: var(--rr-color-primary-hover);
}

.decoration-source-dialog footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
